import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getOvenConfig, listVisionDevices } from '../api/client';
import {
  applyCameraCalibration,
  CameraActualMetadata,
  cameraApiError,
  CameraCalibrationJob,
  CameraConfig,
  CameraConfigRecord,
  CameraFieldError,
  CameraHistoryEntry,
  CameraMetadataResponse,
  CameraStatus,
  CameraTestCaptureJob,
  cancelCameraCalibration,
  createCameraCalibration,
  createCameraTestCapture,
  getCameraCalibration,
  getCameraConfig,
  getCameraHistory,
  getCameraMetadata,
  getCameraStatus,
  getCameraTestCapture,
  listCameraTestCaptures,
  patchCameraConfig,
  reapplyCameraConfig,
  resetCameraConfig,
  restoreCameraConfig,
} from '../api/camera';
import {
  cameraStatusTone,
  cameraStatusRevision,
  cameraDeliveryInfo,
  exposureMsToUs,
  exposureUsToMs,
  isCameraConfigApplied,
  uniqueExposureTimesMs,
  validateCameraConfig,
} from '../utils/cameraSettings';

const OVEN_IDS = ['1', '2'];
const TERMINAL_JOB_STATES = new Set(['succeeded', 'completed', 'failed', 'cancelled']);
const inputClass = 'input py-2 disabled:bg-cream-100 disabled:text-espresso-400 disabled:cursor-not-allowed';

interface DeviceOption {
  id: string;
  ovens: string[];
}

function formatTime(value?: string | null): string {
  return value ? new Date(value).toLocaleString('he-IL') : '—';
}

function showValue(value: unknown, suffix = ''): string {
  return value === null || value === undefined || value === '' ? '—' : `${String(value)}${suffix}`;
}

function FieldShell({
  label,
  help,
  error,
  supported = true,
  children,
}: {
  label: string;
  help: string;
  error?: string;
  supported?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-1" title={help}>
      <span className="flex items-center gap-1 text-xs font-bold text-espresso-700">
        {label} <span className="text-espresso-400 cursor-help">ⓘ</span>
        {!supported && <span className="text-[10px] text-espresso-400">(לא נתמך)</span>}
      </span>
      {children}
      {error && <span className="block text-xs font-bold text-red-600">{error}</span>}
    </label>
  );
}

function Toggle({
  label,
  help,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  help: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      title={help}
      className={`flex items-center gap-3 rounded-xl border p-3 ${
        disabled ? 'bg-cream-100 text-espresso-400' : 'bg-white border-cream-300 text-espresso-800'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="text-sm font-bold">{label}</span>
      <span className="text-espresso-400 cursor-help">ⓘ</span>
    </label>
  );
}

function StatusPanel({
  config,
  status,
  metadata,
}: {
  config: CameraConfigRecord;
  status: CameraStatus | null;
  metadata: CameraMetadataResponse | null;
}) {
  const tone = cameraStatusTone(status);
  const toneClass = {
    ok: 'bg-green-50 border-green-300 text-green-900',
    pending: 'bg-yellow-50 border-yellow-300 text-yellow-900',
    error: 'bg-red-50 border-red-300 text-red-900',
    offline: 'bg-gray-100 border-gray-300 text-gray-800',
  }[tone];
  const toneLabel = {
    ok: 'הגרסה הפעילה אושרה',
    pending: 'ממתין למסירה ואישור מהמצלמה',
    error: 'החלה נכשלה',
    offline: 'המצלמה לא מחוברת',
  }[tone];
  const actual = metadata?.actual || config.actual;
  const deviceMetadata = metadata?.metadata || config.metadata;
  const delivery = cameraDeliveryInfo(status);

  return (
    <section className="card-solid p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold text-espresso-900">מצב הגדרה ומצלמה</h2>
          <p className="text-xs text-espresso-500">
            שמירה, מסירה והחלה הן שלבים נפרדים. רק אישור המצלמה מסמן הגדרה כפעילה.
          </p>
        </div>
        <div className={`rounded-xl border px-3 py-2 text-sm font-bold ${toneClass}`}>
          <span className={`ml-2 inline-block h-2.5 w-2.5 rounded-full ${
            status?.online ? 'bg-green-500' : 'bg-red-400'
          }`} />
          {toneLabel}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="text-xs font-bold text-blue-700">שמור בשרת</div>
          <div className="mt-1 text-2xl font-extrabold">v{config.configurationVersion}</div>
          <div className="text-xs text-blue-700">{formatTime(config.updatedAt)}</div>
        </div>
        <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4">
          <div className="text-xs font-bold text-yellow-800">נמסר למכשיר</div>
          <div className="mt-1 text-2xl font-extrabold">
            {delivery.version == null ? '—' : `v${delivery.version}`}
          </div>
          <div className="text-xs text-yellow-800">מצב מסירה: {showValue(delivery.state)}</div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <div className="text-xs font-bold text-green-800">הוחל בפועל</div>
          <div className="mt-1 text-2xl font-extrabold">
            {status?.lastAppliedVersion == null ? '—' : `v${status.lastAppliedVersion}`}
          </div>
          <div className="text-xs text-green-800">אישור: {formatTime(status?.acknowledgedAt)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
        <div><b>חיבור:</b> {status?.online ? 'מחובר' : 'לא מחובר'}</div>
        <div><b>מימוש:</b> {showValue(deviceMetadata?.implementation)}</div>
        <div><b>חיישן:</b> {showValue(deviceMetadata?.sensorModel)}</div>
        <div><b>גרסה נוכחית:</b> {showValue(status?.lastAppliedVersion)}</div>
        <div><b>סנכרון אחרון:</b> {formatTime(status?.lastSeenAt)}</div>
      </div>

      {status?.status === 'pending' && (
        <div className="rounded-xl bg-yellow-50 p-3 text-sm text-yellow-900">
          פקודה ממתינה: <span dir="ltr">{showValue(status.pendingCommandId)}</span>
        </div>
      )}
      {status?.error && <div className="rounded-xl bg-red-50 p-3 font-bold text-red-700">{status.error}</div>}
      {!!status?.warnings?.length && (
        <ul className="rounded-xl bg-yellow-50 p-3 text-sm text-yellow-900">
          {status.warnings.map((warning) => <li key={warning}>⚠ {warning}</li>)}
        </ul>
      )}
      {!!Object.keys(metadata?.unsupported || config.unsupported).length && (
        <ul className="rounded-xl bg-yellow-50 p-3 text-sm text-yellow-900">
          {Object.entries(metadata?.unsupported || config.unsupported).map(([field, reason]) => (
            <li key={field}>⚠ {field}: {showValue(reason)}</li>
          ))}
        </ul>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-right text-xs text-espresso-500">
            <tr>
              <th className="p-2">פרמטר</th>
              <th className="p-2">מבוקש (שמור)</th>
              <th className="p-2">בפועל (מטא-דאטה)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream-200">
            {[
              ['חשיפה', `${exposureUsToMs(config.config.exposureTimeUs)} ms`, actual?.exposureTimeUs == null ? '—' : `${exposureUsToMs(actual.exposureTimeUs)} ms`],
              ['Gain', config.config.analogueGain, actual?.analogueGain],
              ['משך פריים', `${exposureUsToMs(config.config.frameDurationUs)} ms`, actual?.frameDurationUs == null ? '—' : `${exposureUsToMs(actual.frameDurationUs)} ms`],
              ['AE', config.config.exposureMode, actual?.exposureMode],
              ['מניעת banding', config.config.antiBandingEnabled ? 'פעיל' : 'כבוי', actual?.antiBandingEnabled == null ? '—' : actual.antiBandingEnabled ? 'פעיל' : 'כבוי'],
              ['מחזור הבהוב', showValue(config.config.flickerPeriodUs, ' µs'), showValue(actual?.flickerPeriodUs, ' µs')],
              ['איזון לבן', config.config.whiteBalanceMode, actual?.whiteBalanceMode],
              ['Gain צבע (R / B)', `${showValue(config.config.colourGainRed)} / ${showValue(config.config.colourGainBlue)}`, `${showValue(actual?.colourGainRed)} / ${showValue(actual?.colourGainBlue)}`],
            ].map(([label, requested, applied]) => (
              <tr key={String(label)}>
                <td className="p-2 font-bold">{label}</td>
                <td className="p-2" dir="ltr">{showValue(requested)}</td>
                <td className="p-2" dir="ltr">{showValue(applied)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ConfigForm({
  record,
  busy,
  fieldErrors,
  onSave,
  onReset,
  onReapply,
}: {
  record: CameraConfigRecord;
  busy: boolean;
  fieldErrors: Record<string, string>;
  onSave: (config: CameraConfig) => Promise<void>;
  onReset: () => Promise<void>;
  onReapply: () => Promise<void>;
}) {
  const [draft, setDraft] = useState(record.config);
  useEffect(() => setDraft(record.config), [record]);

  const update = <K extends keyof CameraConfig>(key: K, value: CameraConfig[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };
  const num = (value: string) => value === '' ? 0 : Number(value);
  const nullableNum = (value: string) => value === '' ? null : Number(value);
  const formErrors = validateCameraConfig(draft);
  const errors = { ...formErrors, ...fieldErrors };
  const isSupported = (field: keyof CameraConfig) => !(field in record.unsupported);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (Object.keys(formErrors).length) return;
    await onSave(draft);
  }

  return (
    <form onSubmit={submit} className="card-solid p-5 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold text-espresso-900">הגדרות שמורות</h2>
          <p className="text-xs text-espresso-500">חשיפה ומשך פריים מוצגים במילישניות ונשמרים ב-API במיקרושניות.</p>
        </div>
        <span className="rounded-xl bg-cream-100 px-3 py-2 text-xs text-espresso-600">
          שדות שהמצלמה דיווחה כלא נתמכים מושבתים
        </span>
      </div>

      <section className="rounded-xl border border-cream-300 bg-cream-50 p-4 space-y-4">
        <h3 className="font-extrabold">חשיפה וקצב פריימים</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <FieldShell label="מצב חשיפה (AE)" help="במצב אוטומטי המצלמה קובעת חשיפה ו-Gain" supported={isSupported('exposureMode')}>
            <select className={inputClass} value={draft.exposureMode} disabled={busy || !isSupported('exposureMode')} onChange={(event) => update('exposureMode', event.target.value as CameraConfig['exposureMode'])}>
              <option value="auto">אוטומטי</option>
              <option value="manual">ידני</option>
            </select>
          </FieldShell>
          <FieldShell label="חשיפה (ms)" help="זמן איסוף האור. ה-API מאחסן מיקרושניות." error={errors.exposureTimeUs} supported={isSupported('exposureTimeUs')}>
            <input
              className={inputClass}
              type="number"
              value={exposureUsToMs(draft.exposureTimeUs)}
              min={0.1}
              max={10000}
              step={0.1}
              disabled={busy || draft.exposureMode === 'auto' || !isSupported('exposureTimeUs')}
              onChange={(event) => update('exposureTimeUs', exposureMsToUs(num(event.target.value)))}
              dir="ltr"
            />
            <div className="flex gap-1">
              {[10, 20, 40].map((ms) => (
                <button key={ms} type="button" className="rounded bg-cream-200 px-2 py-1 text-[11px]" disabled={draft.exposureMode === 'auto'} onClick={() => update('exposureTimeUs', exposureMsToUs(ms))}>
                  {ms}ms
                </button>
              ))}
            </div>
          </FieldShell>
          <FieldShell label="Gain אנלוגי" help="הגברת אות החיישן; ערך גבוה מוסיף רעש" error={errors.analogueGain} supported={isSupported('analogueGain')}>
            <input className={inputClass} type="number" min={1} max={64} step={0.1} value={draft.analogueGain} disabled={busy || draft.exposureMode === 'auto' || !isSupported('analogueGain')} onChange={(event) => update('analogueGain', num(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="משך פריים (ms)" help="משך פריים מינימלי; חייב להיות לפחות זמן החשיפה" error={errors.frameDurationUs} supported={isSupported('frameDurationUs')}>
            <input className={inputClass} type="number" value={exposureUsToMs(draft.frameDurationUs)} min={1} max={10000} step={0.1} disabled={busy || !isSupported('frameDurationUs')} onChange={(event) => update('frameDurationUs', exposureMsToUs(num(event.target.value)))} dir="ltr" />
          </FieldShell>
          <Toggle label="Anti-banding" help="מפעיל מנגנון נגד פסי תאורה" checked={draft.antiBandingEnabled} disabled={busy || !isSupported('antiBandingEnabled')} onChange={(value) => update('antiBandingEnabled', value)} />
          <Toggle label="מניעת הבהוב" help="מסנכרן את החשיפה למחזור התאורה" checked={draft.flickerAvoidanceEnabled} disabled={busy || !isSupported('flickerAvoidanceEnabled')} onChange={(value) => update('flickerAvoidanceEnabled', value)} />
          <FieldShell label="FPS" help="מספר פריימים בשנייה" error={errors.framesPerSecond} supported={isSupported('framesPerSecond')}>
            <input className={inputClass} type="number" min={0.1} max={240} step={0.1} value={draft.framesPerSecond ?? ''} disabled={busy || !isSupported('framesPerSecond')} onChange={(event) => update('framesPerSecond', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="מחזור הבהוב (µs)" help="מחזור תאורה ידני למניעת banding" error={errors.flickerPeriodUs} supported={isSupported('flickerPeriodUs')}>
            <input className={inputClass} type="number" min={1000} max={100000} value={draft.flickerPeriodUs} disabled={busy || !draft.flickerAvoidanceEnabled || !isSupported('flickerPeriodUs')} onChange={(event) => update('flickerPeriodUs', num(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="חימום (שניות)" help="זמן המתנה לייצוב המצלמה" error={errors.warmupSeconds} supported={isSupported('warmupSeconds')}>
            <input className={inputClass} type="number" min={0} max={60} step={0.1} value={draft.warmupSeconds} disabled={busy || !isSupported('warmupSeconds')} onChange={(event) => update('warmupSeconds', num(event.target.value))} dir="ltr" />
          </FieldShell>
        </div>
      </section>

      <section className="rounded-xl border border-cream-300 bg-cream-50 p-4 space-y-4">
        <h3 className="font-extrabold">איזון לבן</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <FieldShell label="מצב איזון לבן (WB)" help="אוטומטי, נעול לערכים שנמדדו, או ידני" supported={isSupported('whiteBalanceMode')}>
            <select className={inputClass} value={draft.whiteBalanceMode} disabled={busy || !isSupported('whiteBalanceMode')} onChange={(event) => update('whiteBalanceMode', event.target.value as CameraConfig['whiteBalanceMode'])}>
              <option value="auto">אוטומטי</option>
              <option value="locked">נעול</option>
              <option value="manual">ידני</option>
            </select>
          </FieldShell>
          <FieldShell label="Gain אדום" help="מכפיל ערוץ אדום באיזון לבן ידני" error={errors.colourGainRed} supported={isSupported('colourGainRed')}>
            <input className={inputClass} type="number" min={0.1} max={32} step={0.1} value={draft.colourGainRed ?? ''} disabled={busy || draft.whiteBalanceMode !== 'manual' || !isSupported('colourGainRed')} onChange={(event) => update('colourGainRed', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="Gain כחול" help="מכפיל ערוץ כחול באיזון לבן ידני" error={errors.colourGainBlue} supported={isSupported('colourGainBlue')}>
            <input className={inputClass} type="number" min={0.1} max={32} step={0.1} value={draft.colourGainBlue ?? ''} disabled={busy || draft.whiteBalanceMode !== 'manual' || !isSupported('colourGainBlue')} onChange={(event) => update('colourGainBlue', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
        </div>
      </section>

      <section className="rounded-xl border border-cream-300 bg-cream-50 p-4 space-y-4">
        <h3 className="font-extrabold">עדשה ותמונה</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <FieldShell label="מצב פוקוס" help="אלגוריתם הפוקוס הנתמך במימוש המצלמה" supported={isSupported('focusMode')}>
            <select className={inputClass} value={draft.focusMode ?? ''} disabled={busy || !isSupported('focusMode')} onChange={(event) => update('focusMode', event.target.value ? event.target.value as NonNullable<CameraConfig['focusMode']> : null)}>
              <option value="">ברירת מחדל של המימוש</option>
              {['auto', 'continuous', 'manual', 'fixed'].map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </FieldShell>
          <FieldShell label="מיקום עדשה" help="מיקום פוקוס ידני" error={errors.lensPosition} supported={isSupported('lensPosition')}>
            <input className={inputClass} type="number" min={0} step={0.1} value={draft.lensPosition ?? ''} disabled={busy || draft.focusMode !== 'manual' || !isSupported('lensPosition')} onChange={(event) => update('lensPosition', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="זום" help="מקדם זום דיגיטלי או אופטי" error={errors.zoom} supported={isSupported('zoom')}>
            <input className={inputClass} type="number" min={1} max={32} step={0.1} value={draft.zoom ?? ''} disabled={busy || !isSupported('zoom')} onChange={(event) => update('zoom', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="POV אופקי (Pan)" help="הזזת נקודת המבט ימינה ושמאלה" error={errors.pan} supported={isSupported('pan')}>
            <input className={inputClass} type="number" min={-1} max={1} step={0.01} value={draft.pan ?? ''} disabled={busy || !isSupported('pan')} onChange={(event) => update('pan', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="POV אנכי (Tilt)" help="הזזת נקודת המבט למעלה ולמטה" error={errors.tilt} supported={isSupported('tilt')}>
            <input className={inputClass} type="number" min={-1} max={1} step={0.01} value={draft.tilt ?? ''} disabled={busy || !isSupported('tilt')} onChange={(event) => update('tilt', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="סיבוב" help="סיבוב הפריים במעלות" supported={isSupported('rotation')}>
            <select className={inputClass} value={draft.rotation ?? ''} disabled={busy || !isSupported('rotation')} onChange={(event) => update('rotation', event.target.value === '' ? null : Number(event.target.value) as 0 | 90 | 180 | 270)}>
              <option value="">ללא שינוי</option>
              {[0, 90, 180, 270].map((value) => <option key={value} value={value}>{value}°</option>)}
            </select>
          </FieldShell>
          <FieldShell label="בהירות" help="תיקון בהירות לאחר הלכידה" error={errors.brightness} supported={isSupported('brightness')}>
            <input className={inputClass} type="number" min={-1} max={1} step={0.01} value={draft.brightness ?? ''} disabled={busy || !isSupported('brightness')} onChange={(event) => update('brightness', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="ניגודיות" help="תיקון ניגודיות לאחר הלכידה" error={errors.contrast} supported={isSupported('contrast')}>
            <input className={inputClass} type="number" min={0} max={32} step={0.1} value={draft.contrast ?? ''} disabled={busy || !isSupported('contrast')} onChange={(event) => update('contrast', nullableNum(event.target.value))} dir="ltr" />
          </FieldShell>
          <FieldShell label="רזולוציה" help="גודל פריים בפיקסלים">
            <div className="grid grid-cols-2 gap-2" dir="ltr">
              <input aria-label="רוחב" className={inputClass} type="number" min={160} max={16384} value={draft.resolutionWidth ?? ''} disabled={busy || !isSupported('resolutionWidth')} onChange={(event) => update('resolutionWidth', nullableNum(event.target.value))} />
              <input aria-label="גובה" className={inputClass} type="number" min={120} max={16384} value={draft.resolutionHeight ?? ''} disabled={busy || !isSupported('resolutionHeight')} onChange={(event) => update('resolutionHeight', nullableNum(event.target.value))} />
            </div>
            {(errors.resolutionWidth || errors.resolutionHeight) && <span className="text-xs font-bold text-red-600">{errors.resolutionWidth || errors.resolutionHeight}</span>}
          </FieldShell>
          <Toggle label="היפוך אופקי" help="מראה את הפריים אופקית" checked={draft.horizontalFlip ?? false} disabled={busy || !isSupported('horizontalFlip')} onChange={(value) => update('horizontalFlip', value)} />
          <Toggle label="היפוך אנכי" help="הופך את הפריים אנכית" checked={draft.verticalFlip ?? false} disabled={busy || !isSupported('verticalFlip')} onChange={(value) => update('verticalFlip', value)} />
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className="btn-primary" disabled={busy || Object.keys(formErrors).length > 0}>
          {busy ? 'שומר…' : 'שמור והחל'}
        </button>
        <button type="button" className="btn-secondary" disabled={busy} onClick={() => setDraft(record.config)}>בטל שינויים</button>
        <button type="button" className="btn-secondary" disabled={busy} onClick={onReset}>ברירות מחדל</button>
        <button type="button" className="btn-secondary" disabled={busy} onClick={onReapply}>החל שוב</button>
        <span className="text-xs text-espresso-500">שמירה יוצרת גרסה ממתינה; היא לא תוצג כפעילה לפני acknowledgement.</span>
      </div>
    </form>
  );
}

function MetadataList({ metadata }: { metadata?: CameraActualMetadata | null }) {
  if (!metadata) return <div className="text-sm text-espresso-400">אין מטא-דאטה</div>;
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      <dt>גרסה</dt><dd dir="ltr">{showValue(metadata.configurationVersion)}</dd>
      <dt>חשיפה</dt><dd dir="ltr">{metadata.exposureTimeUs == null ? '—' : `${exposureUsToMs(metadata.exposureTimeUs)} ms`}</dd>
      <dt>Gain</dt><dd dir="ltr">{showValue(metadata.analogueGain)}</dd>
      <dt>משך פריים</dt><dd dir="ltr">{metadata.frameDurationUs == null ? '—' : `${exposureUsToMs(metadata.frameDurationUs)} ms`}</dd>
      <dt>AE / WB</dt><dd dir="ltr">{showValue(metadata.exposureMode)} / {showValue(metadata.whiteBalanceMode)}</dd>
    </dl>
  );
}

function CapturePanel({
  deviceId,
  config,
  status,
  onStatus,
}: {
  deviceId: string;
  config: CameraConfigRecord;
  status: CameraStatus | null;
  onStatus: (status: CameraStatus) => void;
}) {
  const [job, setJob] = useState<CameraTestCaptureJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = true;
    setJob(null);
    setError('');
    return () => { cancelled.current = true; };
  }, [deviceId]);

  async function capture() {
    setBusy(true);
    setError('');
    cancelled.current = false;
    try {
      let latest = status;
      for (let attempt = 0; attempt < 20 && !isCameraConfigApplied(latest); attempt++) {
        if (cancelled.current) return;
        await new Promise((resolve) => setTimeout(resolve, 2000));
        latest = await getCameraStatus(deviceId);
        onStatus(latest);
      }
      if (!isCameraConfigApplied(latest)) {
        throw new Error('לא ניתן לצלם עד שהגרסה השמורה הוחלה ואושרה במצלמה');
      }
      let next = await createCameraTestCapture(deviceId, config.configurationVersion);
      setJob(next);
      while (!TERMINAL_JOB_STATES.has(next.status) && !cancelled.current) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        next = await getCameraTestCapture(deviceId, next.id);
        setJob(next);
      }
      if (!cancelled.current) {
        const captures = await listCameraTestCaptures(deviceId, 25);
        setJob(captures.find((captureJob) => captureJob.id === next.id) || next);
      }
    } catch (captureError) {
      setError(cameraApiError(captureError).message);
    } finally {
      setBusy(false);
    }
  }

  const images = [
    ['תמונה נוכחית', job?.current],
    ['תמונה קודמת', job?.previous],
  ] as const;

  return (
    <section className="card-solid p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold">צילום בדיקה</h2>
          <p className="text-xs text-espresso-500">הצילום יתחיל רק לאחר שהגרסה הפעילה תואמת לגרסה השמורה.</p>
        </div>
        <button className="btn-primary" disabled={busy || !status?.online} onClick={capture}>
          {busy ? `ממתין / מצלם… ${job?.status || ''}` : 'צלם והשווה'}
        </button>
      </div>
      {error && <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div>}
      {job?.error && <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">{job.error}</div>}
      {!!job?.warnings?.length && <div className="rounded-xl bg-yellow-50 p-3 text-sm text-yellow-800">{job.warnings.join(' · ')}</div>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {images.map(([label, image]) => (
          <div key={label} className="rounded-xl border border-cream-300 p-3">
            <h3 className="mb-2 font-bold">{label}</h3>
            {image?.url
              ? <img src={image.url} alt={label} className="aspect-video w-full rounded-lg bg-cream-100 object-contain" />
              : <div className="flex aspect-video items-center justify-center rounded-lg bg-cream-100 text-espresso-400">אין תמונה</div>}
            <div className="mt-2 text-xs text-espresso-500">{formatTime(image?.capturedAt)}</div>
            <MetadataList metadata={image?.metadata} />
          </div>
        ))}
      </div>
    </section>
  );
}

function CalibrationPanel({
  deviceId,
  onApplied,
}: {
  deviceId: string;
  onApplied: (record: CameraConfigRecord) => void;
}) {
  const [exposures, setExposures] = useState([5, 10, 20, 40]);
  const [custom, setCustom] = useState('');
  const [job, setJob] = useState<CameraCalibrationJob | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scoreOrder, setScoreOrder] = useState<'asc' | 'desc'>('asc');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setJob(null);
    setSelectedId(null);
    setError('');
  }, [deviceId]);

  const poll = useCallback(async (id: string) => {
    try {
      const updated = await getCameraCalibration(deviceId, id);
      setJob(updated);
      if (!selectedId && updated.recommendedCandidateId) setSelectedId(updated.recommendedCandidateId);
    } catch (pollError) {
      setError(cameraApiError(pollError).message);
    }
  }, [deviceId, selectedId]);

  useEffect(() => {
    if (!job || TERMINAL_JOB_STATES.has(job.status)) return;
    const timer = window.setInterval(() => poll(job.id), 1500);
    return () => window.clearInterval(timer);
  }, [job, poll]);

  const candidates = useMemo(() => [...(job?.candidates || [])].sort((a, b) =>
    scoreOrder === 'asc' ? a.score - b.score : b.score - a.score
  ), [job?.candidates, scoreOrder]);
  const selected = candidates.find((candidate) => candidate.id === selectedId)
    || candidates.find((candidate) => candidate.id === job?.recommendedCandidateId)
    || candidates[0];

  function addExposure(value: number) {
    setExposures((current) => uniqueExposureTimesMs([...current, value]));
    setCustom('');
  }

  async function start() {
    setBusy(true);
    setError('');
    try {
      const created = await createCameraCalibration(deviceId, exposures.map(exposureMsToUs));
      setJob(created);
      setSelectedId(created.recommendedCandidateId || null);
    } catch (startError) {
      setError(cameraApiError(startError).message);
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!job) return;
    setBusy(true);
    try {
      setJob(await cancelCameraCalibration(deviceId, job.id));
    } catch (cancelError) {
      setError(cameraApiError(cancelError).message);
    } finally {
      setBusy(false);
    }
  }

  async function apply(candidateId?: string) {
    if (!job) return;
    setBusy(true);
    setError('');
    try {
      onApplied(await applyCameraCalibration(deviceId, job.id, candidateId));
    } catch (applyError) {
      setError(cameraApiError(applyError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card-solid p-5 space-y-4">
      <div>
        <h2 className="text-lg font-extrabold">כיול חשיפה</h2>
        <p className="text-xs text-espresso-500">רשימה ייחודית של חשיפות; תוצאות ניתנות למיון ולבחירה לפני החלה.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {exposures.map((exposure) => (
          <button key={exposure} type="button" title="הסר מהרשימה" className="rounded-full border border-gold-400 bg-gold-50 px-3 py-1 text-sm" onClick={() => setExposures((current) => current.filter((value) => value !== exposure))}>
            {exposure}ms ×
          </button>
        ))}
        {[10, 20, 40].map((preset) => (
          <button key={preset} type="button" className="rounded bg-cream-200 px-2 py-1 text-xs" onClick={() => addExposure(preset)}>+ {preset}ms</button>
        ))}
        <form className="flex gap-1" onSubmit={(event) => { event.preventDefault(); addExposure(Number(custom)); }}>
          <input className="input w-28 py-1.5" type="number" min="0.1" step="0.1" value={custom} onChange={(event) => setCustom(event.target.value)} placeholder="ms מותאם" dir="ltr" />
          <button className="btn-secondary py-1.5" disabled={!custom}>הוסף</button>
        </form>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary" disabled={busy || exposures.length === 0 || (!!job && !TERMINAL_JOB_STATES.has(job.status))} onClick={start}>התחל כיול</button>
        {job && !TERMINAL_JOB_STATES.has(job.status) && <button className="btn-secondary" disabled={busy} onClick={cancel}>בטל כיול</button>}
        {job && <span className="text-sm font-bold">מצב: {job.status} · {Math.round(job.progress * 100)}%</span>}
      </div>
      {job && !TERMINAL_JOB_STATES.has(job.status) && (
        <div className="h-2 overflow-hidden rounded-full bg-cream-200">
          <div className="h-full bg-gold-600 transition-all" style={{ width: `${Math.max(0, Math.min(100, job.progress * 100))}%` }} />
        </div>
      )}
      {error && <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div>}
      {job?.error && <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">{job.error}</div>}
      {!!job?.warnings?.length && <div className="rounded-xl bg-yellow-50 p-3 text-sm text-yellow-800">{job.warnings.join(' · ')}</div>}
      {!!candidates.length && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold">מיון ציון:</span>
            <button className={`rounded px-2 py-1 text-xs ${scoreOrder === 'asc' ? 'bg-espresso-800 text-white' : 'bg-cream-200'}`} onClick={() => setScoreOrder('asc')}>נמוך ← גבוה</button>
            <button className={`rounded px-2 py-1 text-xs ${scoreOrder === 'desc' ? 'bg-espresso-800 text-white' : 'bg-cream-200'}`} onClick={() => setScoreOrder('desc')}>גבוה ← נמוך</button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {candidates.map((candidate) => (
              <button key={candidate.id} className={`rounded-xl border p-2 text-right ${candidate.id === selected?.id ? 'border-gold-600 bg-gold-50 ring-2 ring-gold-200' : 'border-cream-300'}`} onClick={() => setSelectedId(candidate.id)}>
                <img src={candidate.image.thumbnailUrl || candidate.image.url} alt={`${exposureUsToMs(candidate.exposureTimeUs)}ms`} className="aspect-video w-full rounded object-cover" />
                <div className="mt-1 text-xs font-bold">{exposureUsToMs(candidate.exposureTimeUs)}ms · {candidate.score.toFixed(2)}</div>
                {(candidate.recommended || candidate.id === job?.recommendedCandidateId) && <span className="text-[10px] font-bold text-green-700">מומלץ</span>}
              </button>
            ))}
          </div>
          {selected && (
            <div className="grid grid-cols-1 gap-4 rounded-xl border border-cream-300 p-3 lg:grid-cols-[minmax(0,2fr)_minmax(240px,1fr)]">
              <img src={selected.image.url} alt="תצוגה מקדימה לכיול" className="max-h-[460px] w-full rounded-lg bg-cream-100 object-contain" />
              <div className="space-y-3">
                <div className="text-xl font-extrabold">{exposureUsToMs(selected.exposureTimeUs)}ms</div>
                <div>ציון: <b>{selected.score.toFixed(3)}</b></div>
                <MetadataList metadata={selected.metadata || selected.image.metadata} />
                <button className="btn-primary w-full" disabled={busy} onClick={() => apply(selected.id)}>החל בחירה</button>
                <button className="btn-secondary w-full" disabled={busy || !job?.recommendedCandidateId} onClick={() => apply()}>החל מומלץ</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function HistoryPanel({
  history,
  currentVersion,
  busy,
  onRestore,
}: {
  history: CameraHistoryEntry[];
  currentVersion: number;
  busy: boolean;
  onRestore: (version: number) => Promise<void>;
}) {
  return (
    <section className="card-solid p-5 space-y-3">
      <div>
        <h2 className="text-lg font-extrabold">היסטוריית גרסאות</h2>
        <p className="text-xs text-espresso-500">שחזור מעתיק גרסה קודמת לגרסה חדשה וממתינה; ההיסטוריה אינה משתנה.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-cream-100 text-right text-xs text-espresso-600">
            <tr>
              <th className="p-2">גרסה</th><th className="p-2">משתמש / זמן</th><th className="p-2">אישור</th>
              <th className="p-2">תוצאה</th><th className="p-2">שגיאות</th><th className="p-2">אזהרות</th><th className="p-2">פעולה</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream-200">
            {history.map((entry) => (
              <tr key={entry.configurationVersion}>
                <td className="p-2 font-extrabold" dir="ltr">v{entry.configurationVersion}{entry.restoredFromVersion != null && <span className="block text-[10px] font-normal">from v{entry.restoredFromVersion}</span>}</td>
                <td className="p-2">{showValue(entry.updatedBy)}<span className="block text-xs text-espresso-500">{formatTime(entry.updatedAt)}</span></td>
                <td className="p-2">{showValue(entry.status)}<span className="block text-xs">{formatTime(entry.acknowledgedAt)}</span></td>
                <td className="p-2">{entry.status === 'active' ? 'הוחל' : entry.status === 'failed' ? 'נכשל' : 'ממתין'}</td>
                <td className="p-2 text-red-700">{entry.errors?.join(' · ') || (entry.status === 'failed' ? 'המצלמה דחתה את הגרסה' : '—')}</td>
                <td className="p-2 text-yellow-800">{entry.warnings?.join(' · ') || (entry.unsupported && Object.keys(entry.unsupported).length ? Object.keys(entry.unsupported).join(' · ') : '—')}</td>
                <td className="p-2"><button className="btn-secondary py-1" disabled={busy || entry.configurationVersion === currentVersion} onClick={() => onRestore(entry.configurationVersion)}>שחזר</button></td>
              </tr>
            ))}
            {!history.length && <tr><td colSpan={7} className="p-6 text-center text-espresso-400">אין היסטוריה</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CameraSettings() {
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [deviceId, setDeviceId] = useState(() => new URLSearchParams(window.location.search).get('deviceId') || '');
  const [record, setRecord] = useState<CameraConfigRecord | null>(null);
  const [status, setStatus] = useState<CameraStatus | null>(null);
  const [metadata, setMetadata] = useState<CameraMetadataResponse | null>(null);
  const [history, setHistory] = useState<CameraHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const statusRevisionRef = useRef('');

  const loadDevices = useCallback(async () => {
    try {
      const visionIds = await listVisionDevices();
      const ovenResults = await Promise.allSettled(OVEN_IDS.map((id) => getOvenConfig(id)));
      const map = new Map<string, Set<string>>();
      visionIds.forEach((id) => map.set(id, new Set()));
      ovenResults.forEach((result) => {
        if (result.status === 'rejected') return;
        const oven = result.value;
        if (!oven.vision_device_id) return;
        if (!map.has(oven.vision_device_id)) map.set(oven.vision_device_id, new Set());
        map.get(oven.vision_device_id)?.add(oven.machine_id);
      });
      const failedOvens = ovenResults.filter((result) => result.status === 'rejected').length;
      if (failedOvens) setError(`לא ניתן לטעון שיוך של ${failedOvens} תנורים`);
      if (deviceId && !map.has(deviceId)) map.set(deviceId, new Set());
      const options = [...map].map(([id, assigned]) => ({ id, ovens: [...assigned] }));
      setDevices(options);
      if (!deviceId && options[0]) setDeviceId(options[0].id);
    } catch (loadError) {
      setError(cameraApiError(loadError).message);
    }
  }, [deviceId]);

  const refresh = useCallback(async (showSpinner = false) => {
    if (!deviceId) return;
    if (showSpinner) setLoading(true);
    try {
      const [nextRecord, nextStatus, nextMetadata, nextHistory] = await Promise.all([
        getCameraConfig(deviceId),
        getCameraStatus(deviceId),
        getCameraMetadata(deviceId),
        getCameraHistory(deviceId),
      ]);
      setRecord(nextRecord);
      setStatus(nextStatus);
      statusRevisionRef.current = cameraStatusRevision(nextStatus);
      setMetadata(nextMetadata);
      setHistory(nextHistory);
      setError('');
    } catch (loadError) {
      setError(cameraApiError(loadError).message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => { loadDevices(); }, [loadDevices]);
  useEffect(() => {
    if (!deviceId) {
      setLoading(false);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('deviceId', deviceId);
    window.history.replaceState(null, '', `${url.pathname}${url.search}`);
    setRecord(null);
    statusRevisionRef.current = '';
    refresh(true);
    let cancelled = false;
    async function poll() {
      try {
        const [nextStatus, nextMetadata] = await Promise.all([
          getCameraStatus(deviceId),
          getCameraMetadata(deviceId),
        ]);
        if (cancelled) return;
        const nextRevision = cameraStatusRevision(nextStatus);
        const statusChanged = nextRevision !== statusRevisionRef.current;
        statusRevisionRef.current = nextRevision;
        setStatus(nextStatus);
        setMetadata(nextMetadata);
        if (statusChanged) {
          const [nextRecord, nextHistory] = await Promise.all([
            getCameraConfig(deviceId),
            getCameraHistory(deviceId),
          ]);
          if (cancelled) return;
          setRecord(nextRecord);
          setHistory(nextHistory);
        }
      } catch (pollError) {
        if (!cancelled) setError(cameraApiError(pollError).message);
      }
    }
    const timer = window.setInterval(poll, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [deviceId, refresh]);

  function handleError(actionError: unknown) {
    const parsed = cameraApiError(actionError);
    setError(parsed.message);
    setFieldErrors(Object.fromEntries(parsed.fieldErrors.map((item: CameraFieldError) => [item.field.replace(/^config\./, ''), item.message])));
  }

  async function action(task: () => Promise<CameraConfigRecord>, success: string) {
    setBusy(true);
    setError('');
    setMessage('');
    setFieldErrors({});
    try {
      const nextRecord = await task();
      setRecord(nextRecord);
      setMessage(success);
      await refresh();
    } catch (actionError) {
      handleError(actionError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5 pb-10">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-espresso-900">הגדרות מצלמה</h1>
          <p className="mt-1 text-sm text-espresso-600">ניהול גרסאות, מצב בפועל, צילום בדיקה וכיול — למנהלים בלבד</p>
        </div>
        <label className="w-full max-w-sm space-y-1">
          <span className="text-xs font-bold text-espresso-700">מצלמה / תנור</span>
          <select className="input" value={deviceId} onChange={(event) => setDeviceId(event.target.value)}>
            {!devices.length && <option value="">לא נמצאו מצלמות</option>}
            {devices.map((device) => <option key={device.id} value={device.id}>{device.id}{device.ovens.length ? ` — תנור ${device.ovens.join(', ')}` : ' — ללא תנור'}</option>)}
          </select>
        </label>
      </header>

      {message && <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm font-bold text-green-800">{message}</div>}
      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-20"><div className="h-12 w-12 animate-spin rounded-full border-b-4 border-gold-700" /></div>
      ) : !deviceId ? (
        <div className="card-solid p-10 text-center text-espresso-500">אין מצלמות מוגדרות. ניתן לפתוח קישור ישיר עם deviceId.</div>
      ) : !record ? (
        <div className="card-solid p-10 text-center text-espresso-500">לא ניתן לטעון את הגדרות המצלמה.</div>
      ) : (
        <>
          <StatusPanel config={record} status={status} metadata={metadata} />
          <ConfigForm
            record={record}
            busy={busy}
            fieldErrors={fieldErrors}
            onSave={(config) => action(() => patchCameraConfig(deviceId, config), 'הגרסה נשמרה ונשלחה. ממתין לאישור המצלמה.')}
            onReset={() => action(() => resetCameraConfig(deviceId), 'נוצרה גרסת ברירות מחדל ממתינה.')}
            onReapply={() => action(() => reapplyCameraConfig(deviceId), 'הגרסה הנוכחית נשלחה שוב. ממתין לאישור.')}
          />
          <CapturePanel deviceId={deviceId} config={record} status={status} onStatus={setStatus} />
          <CalibrationPanel deviceId={deviceId} onApplied={(nextRecord) => {
            setRecord(nextRecord);
            setMessage('בחירת הכיול נשמרה כגרסה חדשה וממתינה לאישור.');
            refresh();
          }} />
          <HistoryPanel
            history={history}
            currentVersion={record.configurationVersion}
            busy={busy}
            onRestore={(version) => action(() => restoreCameraConfig(deviceId, version), `גרסה ${version} שוחזרה לגרסה חדשה וממתינה.`)}
          />
        </>
      )}
    </div>
  );
}

export default CameraSettings;
